<template>
    <div class="step-content">
        <slot name="title" />

        <div class="main-content">
            <div v-if="active" class="input-fields">
                <DsRadio
                    v-model="sendOption"
                    class="timing-radio"
                    :val="$options.SEND_NOW"
                    data-qa="send-now-timing"
                >
                    <div class="radio-option-title">
                        {{ $t('email.broadcast.steps.schedule.sendNowLabel') }}
                    </div>
                </DsRadio>

                <div class="radio-option-description">
                    {{ $t('email.broadcast.steps.schedule.sendNowDescription') }}
                </div>

                <DsRadio
                    v-model="sendOption"
                    class="timing-radio"
                    :val="$options.SEND_LATER"
                    data-qa="send-later-timing"
                >
                    <div class="radio-option-title">
                        {{ $t('email.broadcast.steps.schedule.sendLaterLabel') }}
                    </div>
                </DsRadio>

                <div class="radio-option-description">
                    {{ $t('email.broadcast.steps.schedule.sendLaterDescription') }}
                </div>

                <div v-if="sendOption === $options.SEND_LATER" class="schedule-time">
                    <div class="send-later-date-time-container">
                        <div class="date-container space-after">
                            <DsDatePicker
                                v-model="sendDate"
                                date-only
                                name="basic"
                                disable-before-date="today"
                                position="top"
                                :label="$t('email.broadcast.steps.schedule.date')"
                            />
                        </div>

                        <div class="space-after">
                            {{ $t('global.at') }}
                        </div>

                        <div class="time-container space-after">
                            <DsDatePicker
                                v-model="sendTime"
                                time-only
                                name="time-basic"
                                :label="$t('email.broadcast.steps.schedule.time')"
                            />
                        </div>

                        <div class="space-after">
                            {{ $t('email.broadcast.steps.schedule.in') }}
                        </div>

                        <div class="time-zone-container">
                            <DsMultiselect
                                v-model="timeZone"
                                class="schedule-step-time-zone-options"
                                bind-value-only
                                :label="$t('email.broadcast.steps.schedule.timeZone')"
                                searchable
                                :options="timeZones"
                            />
                        </div>
                    </div>

                    <div v-if="isInPast" class="date-warning">
                        <DsInlineAlert data-qa="past-message-error" leading-icon type="warning">
                            {{ $t('email.broadcast.steps.schedule.pastMessage') }}
                        </DsInlineAlert>
                    </div>
                </div>

                <DsOutlineButton
                    class="save"
                    variant="outline"
                    @click.stop="save"
                >
                    {{ $t('global.save') }}
                </DsOutlineButton>

                <DsTextButton
                    class="cancel"
                    variant="text"
                    @click="cancel"
                >
                    {{ $t('global.cancel') }}
                </DsTextButton>
            </div>

            <EmailBroadcastStepOverview v-if="!active" :sections="overviewSections" />
        </div>

        <slot name="action" :actionText="actionText" />
    </div>
</template>

<script>
import moment from 'moment';
import { getLocalizationInfo } from '@/api/broadcasts/emailBroadcast';
import {
    DATE_FORMAT as BACKEND_DATE_FORMAT,
    TIME_FORMAT as DISPLAY_TIME_FORMAT,
    DISPLAY_DATE_FORMAT_WITH_YEAR,
} from '../../constants/dateFormats.constants';
import { STEPS, STEP_ERRORS } from '@/components/unlayer/constants/editor.constants';
import {
    REQUEST_PAYLOAD_DATE_FORMAT,
    BROADCAST_SCHEDULE_OPTIONS as SEND_OPTIONS,
    DATE_TIME_PARSE_FORMAT,
    SCHEDULE_DATE_FORMAT,
    DATEPICKER_TIME_FORMAT,
    CONTACT_TIME_ZONE_OPTION,
} from '../../constants/emailBroadcast.constants';
import { isScheduleDatePast, mapToArray } from '../../utils/emailBroadcast.util';
import EmailBroadcastStepOverview from './EmailBroadcastStepOverview.vue';

const initialState = () => {
    const sendLaterDate = moment().add(1, 'day');

    return {
        scheduleDate: null,
        scheduleDateWithoutTimeZone: null,
        sendOption: SEND_OPTIONS.NOW,

        sendDate: sendLaterDate.format(BACKEND_DATE_FORMAT),
        sendTime: sendLaterDate.format(DATEPICKER_TIME_FORMAT),
        timeZone: CONTACT_TIME_ZONE_OPTION,
    };
};

export default {
    components: {
        EmailBroadcastStepOverview,
    },

    props: {
        active: Boolean,
        scheduleDateFixEnabled: Boolean,

        email: {
            type: Object,
            required: true,
        },
    },

    SEND_NOW: SEND_OPTIONS.NOW,
    SEND_LATER: SEND_OPTIONS.LATER,

    data() {
        return {
            appTimeZone: '',
            timeZoneOptions: {},
            ...initialState(),
        };
    },

    created() {
        this.syncData(this.email);
        this.fetchLocalizationInfo();
    },

    watch: {
        email: {
            handler(newVal) {
                this.syncData(newVal);
            },
            deep: true,
        },
    },

    computed: {
        actionText() {
            return this.$t('email.broadcast.steps.schedule.actionText');
        },

        timeZones() {
            let timezones = [
                {
                    value: CONTACT_TIME_ZONE_OPTION,
                    label: this.$t('email.broadcast.steps.schedule.contactTimeZone'),
                },
            ];

            // Since we do not use mapState in core we need to calculate the localization in a slightly different way
            //  than in keap web.  This if statement guarantees we have a timeZoneOptions with keys before
            if (Object.keys(this.timeZoneOptions).length > 0) {
                timezones = timezones.concat(this.timeZoneOptions);
            }

            return timezones;
        },

        dateTime() {
            return `${this.sendDate} ${this.sendTime}`;
        },

        useContactTimeZone() {
            return this.timeZone === CONTACT_TIME_ZONE_OPTION;
        },

        isInPast() {
            return isScheduleDatePast({
                scheduleDate: moment(this.dateTime, DATE_TIME_PARSE_FORMAT).format(REQUEST_PAYLOAD_DATE_FORMAT),
                timeZoneId: this.timeZone,
                useContactTimeZone: this.useContactTimeZone,
            });
        },

        shouldSendNow() {
            return this.sendOption === SEND_OPTIONS.NOW;
        },

        sendTimeDisplay() {
            if (this.shouldSendNow) {
                return this.$t('email.broadcast.steps.schedule.sendNow');
            }

            return this.$t(this.useContactTimeZone ? 'email.broadcast.steps.schedule.sendLaterContact' : 'email.broadcast.steps.schedule.sendLater', {
                sendDate: moment(this.sendDate).format(DISPLAY_DATE_FORMAT_WITH_YEAR),
                sendTime: moment(this.sendTime, DATEPICKER_TIME_FORMAT).format(DISPLAY_TIME_FORMAT),
            });
        },

        overviewSections() {
            return [{
                title: this.$t('email.broadcast.steps.schedule.sendTime'),
                content: this.sendTimeDisplay,
                placeholder: this.$t('email.broadcast.steps.schedule.placeholder'),
            }];
        },
    },

    methods: {
        setScheduleDate() {
            if (this.shouldSendNow) {
                this.scheduleDate = null;
            } else {
                if (this.isInPast) {
                    this.scheduleDate = null;

                    return;
                }

                this.scheduleDate = moment(this.dateTime, DATE_TIME_PARSE_FORMAT).format(REQUEST_PAYLOAD_DATE_FORMAT);
                this.scheduleDateWithoutTimeZone = moment(this.dateTime, DATE_TIME_PARSE_FORMAT).format(SCHEDULE_DATE_FORMAT);
            }
        },

        async fetchLocalizationInfo() {
            try {
                const { localizationInfo, timeZoneOptions } = await getLocalizationInfo();

                this.appTimeZone = localizationInfo?.appTimeZone;
                this.timeZoneOptions = mapToArray(timeZoneOptions);
            } catch (e) {
                this.$emit('error', STEP_ERRORS.ERROR_FETCHING_LOCALIZATION_INFO);
            }
        },

        reset() {
            Object.assign(this.$data, initialState());
        },

        cancel() {
            this.reset();
            this.syncData();
            this.$emit('cancel');
        },

        save() {
            this.setScheduleDate();

            const payload = {
                scheduleDate: this.scheduleDate,
                scheduleDateWithoutTimeZone: this.scheduleDateWithoutTimeZone,
                timeZoneId: this.useContactTimeZone ? this.appTimeZone : this.timeZone,
                useContactTimeZone: this.useContactTimeZone,
            };

            this.$emit('save', payload);
        },

        syncData(email = this.email) {
            const {
                scheduleDate,
                scheduleDateWithoutTimeZone,
                useContactTimeZone,
                timeZoneId,
            } = email[STEPS.SCHEDULE];

            this.scheduleDate = scheduleDate;
            this.scheduleDateWithoutTimeZone = scheduleDateWithoutTimeZone;

            if (useContactTimeZone) {
                this.timeZone = CONTACT_TIME_ZONE_OPTION;
            } else if (scheduleDate) {
                this.timeZone = timeZoneId;
            }

            if (!scheduleDate) {
                this.sendOption = SEND_OPTIONS.NOW;
            } else {
                this.sendOption = SEND_OPTIONS.LATER;
                let payloadDate;

                if (this.scheduleDateFixEnabled && scheduleDateWithoutTimeZone) {
                    payloadDate = moment(scheduleDateWithoutTimeZone, SCHEDULE_DATE_FORMAT);
                } else {
                    payloadDate = moment(scheduleDate, REQUEST_PAYLOAD_DATE_FORMAT);
                }
                
                this.sendDate = payloadDate.format(BACKEND_DATE_FORMAT);
                this.sendTime = payloadDate.format(DATEPICKER_TIME_FORMAT);
            }
        },
    },
};
</script>

<style lang="scss" scoped>
@import './steps';

.send-later-date-time-container {
    display: flex;
    align-items: center;
    padding-top: $spacing-200;
    margin-bottom: $spacing-300;

    --input-margin-bottom: 0;
}

.schedule-step-time-zone-options {
    --input-padding-top: 0;
    --input-padding-bottom: 0;
}

.timing-radio {
    padding: $spacing-100 0;
}

.radio-option-title {
    font-weight: $font-weight-bold;
}

.radio-option-description {
    font-size: $font-size-sm;
    margin-bottom: $spacing-200;
    @include margin-start($spacing-300);
}

.time-container {
    flex: 1;
    min-width: px-to-rem(110);
}

.space-after {
    @include margin-end($spacing-100);
}

.schedule-time {
    @include margin-start($spacing-300);
}

.date-warning {
    margin: $spacing-100 0 $spacing-200;
}

.time-zone-container {
    align-items: flex-start;
    padding-top: 0;
}

@media($small) {
    .send-later-date-time-container {
        flex-direction: column;
        align-items: start;

        .space-after {
            margin-bottom: $spacing-100;
        }
    }
}
</style>
